import React, { useEffect, useState, useCallback } from 'react';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import suppliersService from '../../../services/supplierCrm/suppliersService';
import contactsService from '../../../services/supplierCrm/contactsService';
import quotesService from '../../../services/supplierCrm/quotesService';
import { inventoryService, transactionsService } from '../../../services/supplierCrm/inventoryService';

interface AnalyticsData {
  totalSuppliers: number;
  activeSuppliers: number;
  totalContacts: number;
  totalQuotes: number;
  quotePipelineValue: number;
  acceptedQuoteValue: number;
  avgQuoteValue: number;
  suppliersByCategory: Record<string, number>;
  quotesByStatus: Record<string, number>;
  topSuppliersByQuotes: Array<{ name: string; count: number; value: number }>;
  // Inventory metrics
  totalInventoryItems: number;
  totalInventoryValue: number;
  lowStockItemsCount: number;
  outOfStockItemsCount: number;
  totalTransactions: number;
  stockByLocation: Record<string, number>;
}

const AnalyticsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSuppliers: 0,
    activeSuppliers: 0,
    totalContacts: 0,
    totalQuotes: 0,
    quotePipelineValue: 0,
    acceptedQuoteValue: 0,
    avgQuoteValue: 0,
    suppliersByCategory: {},
    quotesByStatus: {},
    topSuppliersByQuotes: [],
    totalInventoryItems: 0,
    totalInventoryValue: 0,
    lowStockItemsCount: 0,
    outOfStockItemsCount: 0,
    totalTransactions: 0,
    stockByLocation: {}
  });

  const fetchAnalytics = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const [suppliersRes, contactsRes, quotesRes, inventoryRes, transactionsRes] = await Promise.all([
        suppliersService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
        contactsService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
        quotesService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
        inventoryService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
        transactionsService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 })
      ]);

      const suppliers = suppliersRes.data || [];
      const contacts = contactsRes.data || [];
      const quotes = quotesRes.data || [];
      const inventory = inventoryRes.data || [];
      const transactions = transactionsRes.data || [];

      // Calculate metrics
      const activeCount = suppliers.filter(s => s.status === 'active').length;
      const pipelineValue = quotes
        .filter(q => q.status === 'pending' || q.status === 'draft')
        .reduce((sum, q) => sum + (q.amount || 0), 0);
      const acceptedValue = quotes
        .filter(q => q.status === 'accepted')
        .reduce((sum, q) => sum + (q.amount || 0), 0);
      const avgValue = quotes.length > 0 
        ? quotes.reduce((sum, q) => sum + (q.amount || 0), 0) / quotes.length
        : 0;

      // Category distribution
      const categoryDist: Record<string, number> = {};
      suppliers.forEach(s => {
        const cat = s.category || 'Uncategorized';
        categoryDist[cat] = (categoryDist[cat] || 0) + 1;
      });

      // Quote status distribution
      const statusDist: Record<string, number> = {};
      quotes.forEach(q => {
        const status = q.status || 'draft';
        statusDist[status] = (statusDist[status] || 0) + 1;
      });

      // Top suppliers by quote value
      const supplierQuoteMap: Record<string, { name: string; count: number; value: number }> = {};
      quotes.forEach(q => {
        const supplier = suppliers.find(s => s.id === q.supplier_id);
        if (supplier) {
          if (!supplierQuoteMap[supplier.id]) {
            supplierQuoteMap[supplier.id] = { name: supplier.name, count: 0, value: 0 };
          }
          supplierQuoteMap[supplier.id].count++;
          supplierQuoteMap[supplier.id].value += q.amount || 0;
        }
      });
      const topSuppliers = Object.values(supplierQuoteMap)
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      // Inventory calculations
      const lowStockCount = inventory.filter(item => item.current_stock <= item.reorder_point).length;
      const outOfStockCount = inventory.filter(item => item.current_stock === 0).length;
      
      // Calculate total inventory value (using latest transaction costs)
      let totalInventoryValue = 0;
      inventory.forEach(item => {
        // Find most recent transaction with unit_cost for this item
        const itemTransactions = transactions.filter(t => t.inventory_item_id === item.id && t.unit_cost);
        const latestCost = itemTransactions.length > 0 
          ? itemTransactions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].unit_cost
          : 0;
        totalInventoryValue += (latestCost || 0) * item.current_stock;
      });

      // Stock by location
      const locationDist: Record<string, number> = {};
      inventory.forEach(item => {
        const loc = item.location || 'Unknown';
        locationDist[loc] = (locationDist[loc] || 0) + item.current_stock;
      });

      setAnalytics({
        totalSuppliers: suppliers.length,
        activeSuppliers: activeCount,
        totalContacts: contacts.length,
        totalQuotes: quotes.length,
        quotePipelineValue: pipelineValue,
        acceptedQuoteValue: acceptedValue,
        avgQuoteValue: avgValue,
        suppliersByCategory: categoryDist,
        quotesByStatus: statusDist,
        topSuppliersByQuotes: topSuppliers,
        totalInventoryItems: inventory.length,
        totalInventoryValue,
        lowStockItemsCount: lowStockCount,
        outOfStockItemsCount: outOfStockCount,
        totalTransactions: transactions.length,
        stockByLocation: locationDist
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Total Suppliers" value={analytics.totalSuppliers} />
        <MetricCard label="Active Suppliers" value={analytics.activeSuppliers} />
        <MetricCard label="Total Contacts" value={analytics.totalContacts} />
        <MetricCard label="Total Quotes" value={analytics.totalQuotes} />
      </div>

      {/* Financial Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard 
          label="Pipeline Value" 
          value={`$${(analytics.quotePipelineValue / 1000).toFixed(1)}K`} 
          valueColor="text-yellow-600"
        />
        <MetricCard 
          label="Accepted Value" 
          value={`$${(analytics.acceptedQuoteValue / 1000).toFixed(1)}K`}
          valueColor="text-green-600"
        />
        <MetricCard 
          label="Avg Quote Value" 
          value={`$${analytics.avgQuoteValue.toFixed(0)}`}
          valueColor="text-blue-600"
        />
      </div>

      {/* Inventory Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard 
          label="📦 Total Inventory Items" 
          value={analytics.totalInventoryItems}
        />
        <MetricCard 
          label="💰 Total Inventory Value" 
          value={`$${(analytics.totalInventoryValue / 1000).toFixed(1)}K`}
          valueColor="text-purple-600"
        />
        <MetricCard 
          label="⚠️ Low Stock Items" 
          value={analytics.lowStockItemsCount}
          valueColor="text-orange-600"
        />
        <MetricCard 
          label="🚨 Out of Stock" 
          value={analytics.outOfStockItemsCount}
          valueColor="text-red-600"
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Suppliers by Category */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">Suppliers by Category</h3>
          <div className="space-y-3">
            {Object.entries(analytics.suppliersByCategory).length === 0 ? (
              <p className="text-sm text-gray-500">No data available</p>
            ) : (
              Object.entries(analytics.suppliersByCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{category}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-32 overflow-hidden rounded-full bg-gray-200">
                        <div 
                          className="h-full bg-primary"
                          style={{ width: `${(count / analytics.totalSuppliers) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{count}</span>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Quotes by Status */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">Quotes by Status</h3>
          <div className="space-y-3">
            {Object.entries(analytics.quotesByStatus).length === 0 ? (
              <p className="text-sm text-gray-500">No quotes yet</p>
            ) : (
              Object.entries(analytics.quotesByStatus)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm capitalize text-gray-700">{status}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-32 overflow-hidden rounded-full bg-gray-200">
                        <div 
                          className={`h-full ${getStatusColor(status)}`}
                          style={{ width: `${(count / analytics.totalQuotes) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{count}</span>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Inventory Distribution */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Stock by Location */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">📍 Stock by Location</h3>
          <div className="space-y-3">
            {Object.entries(analytics.stockByLocation).length === 0 ? (
              <p className="text-sm text-gray-500">No inventory data</p>
            ) : (
              Object.entries(analytics.stockByLocation)
                .sort((a, b) => b[1] - a[1])
                .map(([location, count]) => {
                  const totalStock = Object.values(analytics.stockByLocation).reduce((sum, c) => sum + c, 0);
                  return (
                    <div key={location} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{location}</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-32 overflow-hidden rounded-full bg-gray-200">
                          <div 
                            className="h-full bg-purple-500"
                            style={{ width: `${(count / totalStock) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{count}</span>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Inventory Health */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">📊 Inventory Health</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Total Items</span>
              <span className="text-lg font-bold text-gray-900">{analytics.totalInventoryItems}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Low Stock</span>
              <span className="text-lg font-bold text-orange-600">{analytics.lowStockItemsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Out of Stock</span>
              <span className="text-lg font-bold text-red-600">{analytics.outOfStockItemsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Total Transactions</span>
              <span className="text-lg font-bold text-blue-600">{analytics.totalTransactions}</span>
            </div>
            {analytics.totalInventoryItems > 0 && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3">
                <div className="text-xs text-gray-600">Stock Health Score</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <div 
                      className={`h-full ${analytics.lowStockItemsCount / analytics.totalInventoryItems > 0.3 ? 'bg-red-500' : analytics.lowStockItemsCount / analytics.totalInventoryItems > 0.1 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.max(10, 100 - (analytics.lowStockItemsCount / analytics.totalInventoryItems * 100))}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold">{Math.round(100 - (analytics.lowStockItemsCount / analytics.totalInventoryItems * 100))}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Suppliers */}
      <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">🏆 Top Suppliers by Quote Value</h3>
        {analytics.topSuppliersByQuotes.length === 0 ? (
          <p className="text-sm text-gray-500">No quote data available</p>
        ) : (
          <div className="space-y-3">
            {analytics.topSuppliersByQuotes.map((supplier, index) => (
              <div key={supplier.name} className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-gray-400">#{index + 1}</span>
                  <div>
                    <div className="font-semibold text-gray-900">{supplier.name}</div>
                    <div className="text-xs text-gray-500">{supplier.count} quotes</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">
                    ${(supplier.value / 1000).toFixed(1)}K
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: number | string;
  valueColor?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, valueColor = 'text-gray-900' }) => {
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${valueColor}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
};

function getStatusColor(status: string): string {
  switch (status) {
    case 'accepted': return 'bg-green-500';
    case 'rejected': return 'bg-red-500';
    case 'pending': return 'bg-yellow-500';
    case 'expired': return 'bg-gray-500';
    default: return 'bg-blue-500';
  }
}

export default AnalyticsTab;
